import React, { useEffect, useMemo, useState } from 'react';
import './AssignedProperties.css';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import {
  acceptAgentAssignment,
  getMyAgentAssignments,
  getPendingAgentAssignments,
  rejectAgentAssignment,
} from '../../../api/services/agentAssignmentsService';

const AssignedProperties = ({ onNotify, onNavigate }) => {
  const [assignments, setAssignments] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingErrorMessage, setPendingErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState('assigned'); // assigned | pending
  const [actionLoadingById, setActionLoadingById] = useState({});

  useEffect(() => {
    const fetchAssigned = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await getMyAgentAssignments({ page: 0, size: 20 });
        const content = response?.data?.content || [];
        setAssignments(Array.isArray(content) ? content : []);
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setErrorMessage(error.message || 'Failed to load assignments. Please try again.');
        setAssignments([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchPending = async () => {
      setPendingLoading(true);
      setPendingErrorMessage('');
      try {
        const response = await getPendingAgentAssignments();
        const list = response?.data || [];
        setPendingInvitations(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error fetching pending invitations:', error);
        setPendingErrorMessage(error.message || 'Failed to load pending invitations. Please try again.');
        setPendingInvitations([]);
      } finally {
        setPendingLoading(false);
      }
    };

    fetchAssigned();
    fetchPending();
  }, []);

  const assignedList = useMemo(() => {
    const list = Array.isArray(assignments) ? assignments : [];
    return list;
  }, [assignments]);

  const pendingList = useMemo(() => {
    const list = Array.isArray(pendingInvitations) ? pendingInvitations : [];
    return list;
  }, [pendingInvitations]);

  const handleManageTenants = (assignment) => {
    // Store minimal property data for the new page
    localStorage.setItem('agent_selected_property', JSON.stringify({
      id: assignment.propertyId,
      name: assignment.propertyName,
      address: assignment.propertyAddress,
    }));
    // Navigate to manage tenants page
    if (onNavigate) onNavigate('manage-tenants');
    if (onNotify) onNotify(`Opening manage tenants for ${assignment.propertyName}`);
  };

  const handleFollowPayments = (assignment) => {
    // Store minimal property data for the new page
    localStorage.setItem('agent_selected_property', JSON.stringify({
      id: assignment.propertyId,
      name: assignment.propertyName,
      address: assignment.propertyAddress,
    }));
    // Navigate to rent status page
    if (onNavigate) onNavigate('follow-payments');
    if (onNotify) onNotify(`Opening rent status for ${assignment.propertyName}`);
  };

  const refreshLists = async () => {
    setErrorMessage('');
    setPendingErrorMessage('');
    setLoading(true);
    setPendingLoading(true);
    try {
      const [assignedRes, pendingRes] = await Promise.all([
        getMyAgentAssignments({ page: 0, size: 20 }),
        getPendingAgentAssignments(),
      ]);

      const assignedContent = assignedRes?.data?.content || [];
      setAssignments(Array.isArray(assignedContent) ? assignedContent : []);

      const pendingData = pendingRes?.data || [];
      setPendingInvitations(Array.isArray(pendingData) ? pendingData : []);
    } catch (error) {
      console.error('Error refreshing lists:', error);
      const msg = error.message || 'Failed to refresh. Please try again.';
      setErrorMessage(msg);
      setPendingErrorMessage(msg);
    } finally {
      setLoading(false);
      setPendingLoading(false);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const handleAccept = async (assignmentId) => {
    if (!assignmentId) return;
    setActionLoadingById((prev) => ({ ...prev, [assignmentId]: 'accept' }));
    try {
      const res = await acceptAgentAssignment(assignmentId);
      if (onNotify) onNotify(res?.message || 'Invitation accepted');
      await refreshLists();
      setActiveTab('assigned');
    } catch (error) {
      console.error('Accept invitation error:', error);
      if (onNotify) onNotify(error.message || 'Failed to accept invitation');
      setPendingErrorMessage(error.message || 'Failed to accept invitation');
    } finally {
      setActionLoadingById((prev) => {
        const next = { ...prev };
        delete next[assignmentId];
        return next;
      });
    }
  };

  const handleReject = async (assignmentId) => {
    if (!assignmentId) return;
    setActionLoadingById((prev) => ({ ...prev, [assignmentId]: 'reject' }));
    try {
      const res = await rejectAgentAssignment(assignmentId);
      if (onNotify) onNotify(res?.message || 'Invitation declined');
      await refreshLists();
    } catch (error) {
      console.error('Reject invitation error:', error);
      if (onNotify) onNotify(error.message || 'Failed to decline invitation');
      setPendingErrorMessage(error.message || 'Failed to decline invitation');
    } finally {
      setActionLoadingById((prev) => {
        const next = { ...prev };
        delete next[assignmentId];
        return next;
      });
    }
  };

  return (
    <div className="assigned-properties-container">
      <CollapsibleTopPanel id="agent-assigned-properties-top-panel" defaultOpenOnMobile={false}>
        <div className="assigned-properties-header">
          <div className="assigned-properties-title">
            <h2>My Properties</h2>
            <div className="assigned-properties-subtitle">
              Switch between your active assignments and pending invitations.
            </div>
          </div>
          <div className="assigned-properties-filter">
            <label className="filter-label" htmlFor="agent-assignments-filter">
              Filter
            </label>
            <select
              id="agent-assignments-filter"
              className="filter-select"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              <option value="assigned">Assigned ({assignedList.length})</option>
              <option value="pending">Pending Invitations ({pendingList.length})</option>
            </select>
          </div>
        </div>
      </CollapsibleTopPanel>

      {errorMessage && (
        <div className="empty-state" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
          {errorMessage}
        </div>
      )}

      {activeTab === 'assigned' ? (
        loading ? (
          <div className="empty-state">Loading assigned properties...</div>
        ) : assignedList.length === 0 ? (
          <div className="empty-state">No properties assigned to you yet.</div>
        ) : (
          <div className="properties-grid">
            {assignedList.map((assignment) => {
              return (
                <div key={assignment.id} className="property-card card">
                  <div className="property-header">
                    <h3>{assignment.propertyName}</h3>
                    <span className="property-owner">{assignment.propertyAddress || 'No address provided'}</span>
                  </div>
                  <div className="property-stats">
                    <div className="stat-item">
                      <span className="stat-label">Role</span>
                      <span className="stat-value">{assignment.role || '—'}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Status</span>
                      <span className="stat-value">{assignment.status || '—'}</span>
                    </div>
                  </div>
                  <div className="property-actions">
                    <button className="btn btn-secondary" onClick={() => handleManageTenants(assignment)}>
                      Manage Tenants
                    </button>
                    <button className="btn btn-secondary" onClick={() => handleFollowPayments(assignment)}>
                      Rent Status
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <>
          {pendingErrorMessage && (
            <div className="empty-state" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
              {pendingErrorMessage}
            </div>
          )}
          {pendingLoading ? (
            <div className="empty-state">Loading pending invitations...</div>
          ) : pendingList.length === 0 ? (
            <div className="empty-state">No pending invitations right now.</div>
          ) : (
            <div className="properties-grid">
              {pendingList.map((inv) => {
                const busy = actionLoadingById[inv.id];
                return (
                  <div key={inv.id} className="property-card card invitation-card">
                    <div className="property-header">
                      <div className="invitation-title-row">
                        <h3>{inv.propertyName}</h3>
                        <span className="status-badge pending">{inv.status || 'PENDING'}</span>
                      </div>
                      <span className="property-owner">{inv.propertyAddress || 'No address provided'}</span>
                    </div>

                    <div className="invitation-meta">
                      <div className="meta-item">
                        <span className="meta-label">Invited by</span>
                        <span className="meta-value">{inv.assignedByName || '—'}</span>
                        <span className="meta-subvalue">{inv.assignedByEmail || ''}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Role</span>
                        <span className="meta-value">{inv.role || '—'}</span>
                        <span className="meta-subvalue">Assigned {formatDateTime(inv.assignedAt)}</span>
                      </div>
                    </div>

                    <div className="invitation-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleAccept(inv.id)}
                        disabled={!!busy}
                      >
                        {busy === 'accept' ? 'Accepting...' : 'Accept'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleReject(inv.id)}
                        disabled={!!busy}
                      >
                        {busy === 'reject' ? 'Declining...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AssignedProperties;
